import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Tooltip,
  MenuItem,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  AdminPanelSettings as AdminIcon,
  ExitToApp as LogoutIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
    navigate('/login');
  };

  const handleViewProfile = () => {
    handleCloseUserMenu();
    navigate('/profile');
  };

  const handleEditProfile = () => {
    handleCloseUserMenu();
    navigate('/profile/edit');
  };

  const handleMenuExpand = (menuText) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText]
    }));
  };

  const toggleDrawer = (open) => (event) => {
    // Handle keyboard and click events
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setDrawerOpen(open !== undefined ? open : !drawerOpen);
  };

  // Enhanced drawer close handler for better cross-browser compatibility
  const handleDrawerClose = (event, reason) => {
    // Close on backdrop click, escape key, or any other reason
    if (reason === 'backdropClick' || reason === 'escapeKeyDown' || !reason) {
      setDrawerOpen(false);
    }
  };

  // Handle ESC key to close drawer
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && drawerOpen) {
        setDrawerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerOpen]);

  // Global click listener for Edge browser compatibility
  React.useEffect(() => {
    if (!drawerOpen || !isMobile) return;

    const handleGlobalClick = (event) => {
      // Get the drawer paper element
      const drawerPaper = document.querySelector('.MuiDrawer-paper');
      const menuButton = document.querySelector('[aria-label="menu"]');
      
      if (drawerPaper && menuButton) {
        // Check if click is outside drawer and not on menu button
        if (!drawerPaper.contains(event.target) && !menuButton.contains(event.target)) {
          setDrawerOpen(false);
        }
      }
    };

    // Add listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleGlobalClick, true);
      document.addEventListener('touchend', handleGlobalClick, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('touchend', handleGlobalClick, true);
    };
  }, [drawerOpen, isMobile]);

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      permission: null
    },
    {
      text: 'Manage Admins',
      icon: <AdminIcon />,
      path: '/admin-management',
      permission: 'system.manage'
    },
    {
      text: 'Permission Settings',
      icon: <SettingsIcon />,
      path: '/permission-settings',
      permission: 'system.manage'
    },
    {
      text: 'Customers',
      icon: <PeopleIcon />,
      path: '/customers',
      permission: 'customers.view'
    },
    {
      text: 'Transactions',
      icon: <ReceiptIcon />,
      path: '/transactions',
      permission: 'transactions.view'
    },
    {
      text: 'Passbook Print',
      icon: <PrintIcon />,
      path: '/passbook',
      permission: 'passbook.print'
    },
    {
      text: 'First Page Print',
      icon: <ViewIcon />,
      path: '/first-page-print',
      permission: 'passbook.print'
    },
    {
      text: 'Reports',
      icon: <ReportsIcon />,
      path: null,
      permission: 'transactions.view',
      isGroup: true,
      children: [
        {
          text: 'Gas Transaction Report',
          icon: <ReceiptIcon />,
          path: '/reports/gas-transactions',
          permission: 'transactions.view'
        },
        {
          text: 'Customer Report',
          icon: <PeopleIcon />,
          path: '/reports/customers',
          permission: 'customers.view'
        }
      ]
    },
    {
      text: 'Main Menu Settings',
      icon: <PrintIcon />,
      path: null,
      permission: 'passbook.print',
      isGroup: true,
      children: [
        {
          text: 'Passbook First Page Settings',
          icon: <EditIcon />,
          path: '/first-page-settings',
          permission: 'passbook.print'
        },
        {
          text: 'Transaction Print Settings',
          icon: <SettingsIcon />,
          path: '/transaction-settings',
          permission: 'passbook.print'
        }
      ]
    }
  ];

  // Filter menu items based on permissions and user role
  const availableMenuItems = menuItems.filter(item => {
    // Skip permission check if no permission required (like Dashboard)
    if (!item.permission) return true;
    
    // SuperAdmin has access to all permissions
    if (user?.roles?.includes('SuperAdmin')) {
      return true;
    }
    
    // Check permission for all other users
    return hasPermission(item.permission);
  });
  


  const handleMenuItemClick = (path) => {
    navigate(path);
    // Always close the drawer after clicking a menu item
    setDrawerOpen(false);
  };

  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
          Gas Passbook System
        </Typography>
        {user && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mt: 1 }}>
            {user.firstName} {user.lastName}
          </Typography>
        )}
      </Box>
      
      {/* Menu Items */}
      <List sx={{ flex: 1, pt: 2 }}>
        {availableMenuItems.map((item) => (
          <div key={item.text}>
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => item.isGroup ? handleMenuExpand(item.text) : handleMenuItemClick(item.path)}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  color: 'white'
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      fontWeight: 500,
                      color: 'white'
                    } 
                  }} 
                />
                {item.isGroup && (
                  expandedMenus[item.text] ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />
                )}
              </ListItemButton>
            </ListItem>
            {item.isGroup && (
              <Collapse in={expandedMenus[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children?.map((childItem) => (
                    <ListItem key={childItem.text} disablePadding>
                      <ListItemButton 
                        onClick={() => handleMenuItemClick(childItem.path)}
                        sx={{ 
                          py: 1, 
                          px: 3,
                          pl: 6, // Indent child items
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          color: 'white'
                        }}
                      >
                        <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                          {childItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={childItem.text} 
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontWeight: 400,
                              color: 'rgba(255, 255, 255, 0.9)',
                              fontSize: '0.9rem'
                            } 
                          }} 
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </List>
      
      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.12)', mt: 'auto' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', display: 'block' }}>
          Version 1.0 - SuperAdmin Dashboard
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Desktop Sidebar - Now toggleable with click-outside */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          BackdropProps: {
            onClick: (e) => {
              e.stopPropagation();
              setDrawerOpen(false);
            },
            onTouchEnd: (e) => {
              e.stopPropagation();
              setDrawerOpen(false);
            },
            sx: { 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer'
            }
          }
        }}
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: theme.palette.primary.main,
            color: 'white'
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
          disableEnforceFocus: true, // Helps with focus management in Edge
          disableAutoFocus: true, // Prevents auto-focus issues
          BackdropProps: {
            onClick: (e) => {
              e.stopPropagation();
              setDrawerOpen(false);
            },
            onTouchEnd: (e) => {
              e.stopPropagation();
              setDrawerOpen(false);
            },
            sx: { 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer'
            }
          }
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: theme.palette.primary.main,
            color: 'white'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%' // Full width since sidebar is now toggleable
        }}
      >
        {/* Top AppBar */}
        <AppBar 
          position="sticky" 
          elevation={1}
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer - 1,
            width: '100%' // Full width since sidebar is now toggleable
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {/* Menu Button for both Mobile and Desktop */}
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ flexGrow: 1 }}
              >
                Gas Passbook System
              </Typography>

              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Tooltip title="Account settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={handleViewProfile}>
                    <ViewIcon sx={{ mr: 1 }} />
                    View Profile
                  </MenuItem>
                  <MenuItem onClick={handleEditProfile}>
                    <EditIcon sx={{ mr: 1 }} />
                    Edit Profile
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
